import ApiService from "src/util/api.service";

export interface PrivacyPolicyRecord {
  id?: string | number;
  version?: string;
  content?: string;
  status?: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  effectiveDate?: string;
}

interface PrivacyPolicyResponse<T = PrivacyPolicyRecord | PrivacyPolicyRecord[]> {
  response?: T;
}

export interface PrivacyPolicyAcceptanceStatus {
  acceptanceRequired: boolean;
  lastAcceptedPolicyId?: string | number | null;
  policyToAccept?: PrivacyPolicyRecord | null;
}

export const PRIVACY_POLICY_ENDPOINTS = {
  MANAGEMENT: "/crm-client/api/v1/privacy-policies",
  LATEST: "/crm-client/api/v1/privacy-policies/public/latest",
  ACCEPTANCE_STATUS: (userId: string | number) =>
    `/crm-client/api/v1/privacy-policies/users/${userId}/acceptance-status`,
  ACCEPT: (userId: string | number) =>
    `/crm-client/api/v1/privacy-policies/users/${userId}/acceptances`,
} as const;

const asRecords = (response: PrivacyPolicyResponse["response"]): PrivacyPolicyRecord[] =>
  Array.isArray(response) ? response : response ? [response] : [];

const latestRecord = (records: PrivacyPolicyRecord[]) =>
  [...records].sort((a, b) => {
    const dateA = Date.parse(a.updatedAt ?? a.createdAt ?? a.publishedAt ?? "");
    const dateB = Date.parse(b.updatedAt ?? b.createdAt ?? b.publishedAt ?? "");
    if (dateA !== dateB) return dateB - dateA;
    return Number(b.id ?? 0) - Number(a.id ?? 0);
  })[0] ?? null;

export const getManagedPrivacyPolicy = async (
  language?: string
): Promise<PrivacyPolicyRecord | null> => {
  const { data } = await ApiService.get<PrivacyPolicyResponse>(PRIVACY_POLICY_ENDPOINTS.LATEST, {
    headers: language ? { "Accept-Language": language } : undefined,
  });
  return latestRecord(asRecords(data.response));
};

export const saveManagedPrivacyPolicy = async (content: string): Promise<PrivacyPolicyRecord> => {
  const { data } = await ApiService.post<PrivacyPolicyResponse>(
    PRIVACY_POLICY_ENDPOINTS.MANAGEMENT,
    { content }
  );
  const created = asRecords(data.response)[0];
  if (!created?.id) throw new Error("Privacy policy id was not returned by the API.");
  return created;
};

export const publishManagedPrivacyPolicy = (id: string | number) =>
  ApiService.post(`${PRIVACY_POLICY_ENDPOINTS.MANAGEMENT}/${id}/publish`);

export const getPrivacyPolicyAcceptanceStatus = async (
  userId: string | number,
): Promise<PrivacyPolicyAcceptanceStatus | null> => {
  const { data } = await ApiService.get<PrivacyPolicyResponse<PrivacyPolicyAcceptanceStatus>>(
    PRIVACY_POLICY_ENDPOINTS.ACCEPTANCE_STATUS(userId),
  );
  return data.response ?? null;
};

export const acceptPrivacyPolicy = (userId: string | number, privacyPolicyId: string | number) =>
  ApiService.post(PRIVACY_POLICY_ENDPOINTS.ACCEPT(userId), { privacyPolicyId });

export const extractFirstParagraph = (markdown = "") =>
  markdown
    .replace(/^(?:\s*#{1,6}\s+[^\r\n]+\r?\n+)+/, "")
    .split(/\r?\n\s*\r?\n/)[0]
    ?.replace(/\r?\n/g, " ")
    .trim() ?? "";
